import React from "react";
import {
  Layers, PackageX, KeyRound, CalendarClock, PieChart,
  TrendingDown, AlertTriangle, ArrowRight,
} from "lucide-react";

/**
 * Result cards for the TRM assistant.
 *
 * Kept out of ChatBot.jsx (already 1,000+ lines) so the tool-analysis cards and
 * these stay separately readable.
 *
 * numbers and the badge is what stops them being mistaken for real spend in a
 * screenshot or a demo.
 */

const money = (n) =>
  n === null || n === undefined ? "—"
  : Math.abs(n) >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M`
  : Math.abs(n) >= 1_000 ? `$${(n / 1_000).toFixed(0)}K`
  : `$${Number(n).toFixed(0)}`;

const num = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString());

const CARD = "rounded-xl border border-gray-200 bg-white overflow-hidden";

function Head({ icon: Icon, title, right }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-4 py-2.5">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
        <Icon size={16} className="text-indigo-600" /> {title}
      </div>
      <div className="flex items-center gap-2">{right}</div>
    </div>
  );
}

/** Utilisation drives every recommendation here, so colour it consistently. */
function UtilBar({ pct }) {
  const tone = pct < 25 ? "bg-red-500" : pct < 60 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 shrink-0 rounded-full bg-gray-100">
        <div className={`h-1.5 rounded-full ${tone}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <span className="tabular-nums text-xs text-gray-600">{pct}%</span>
    </div>
  );
}

function Stat({ label, value, tone = "gray" }) {
  const tones = { gray: "text-gray-900", red: "text-red-600", green: "text-emerald-600" };
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`mt-0.5 text-lg font-semibold tabular-nums ${tones[tone]}`}>{value}</div>
    </div>
  );
}

export function SummaryCard({ data }) {
  return (
    <div className={CARD}>
      <Head icon={PieChart} title="Portfolio overview" />
      <div className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-4">
        <Stat label="Products" value={num(data.products)} />
        <Stat label="Annual spend" value={money(data.annualSpend)} />
        <Stat label="Shelfware" value={money(data.shelfwareCost)} tone="red" />
        <Stat label="Identified savings" value={money(data.identifiedSavings)} tone="green" />
      </div>
      <div className="px-4 pb-4">
        <div className="mb-2 flex items-center justify-between text-xs text-gray-500">
          <span>Spend by category</span>
          <span>{data.shelfwarePctOfSpend}% of spend is unused licences</span>
        </div>
        <div className="space-y-1.5">
          {(data.byCategory || []).slice(0, 6).map((c) => {
            const top = data.byCategory[0]?.annual_cost || 1;
            return (
              <div key={c.category} className="flex items-center gap-3">
                <span className="w-28 shrink-0 truncate text-xs text-gray-700">{c.category}</span>
                <div className="h-2 flex-1 rounded-full bg-gray-100">
                  <div className="h-2 rounded-full bg-indigo-500"
                       style={{ width: `${(c.annual_cost / top) * 100}%` }} />
                </div>
                <span className="w-16 shrink-0 text-right tabular-nums text-xs text-gray-600">
                  {money(c.annual_cost)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function ConsolidationCard({ data }) {
  return (
    <div className={CARD}>
      <Head icon={Layers} title={`${data.count} consolidation opportunities`}
            right={<span className="text-xs font-semibold text-emerald-700">
              {money(data.totalSavings)}/yr
            </span>} />
      <div className="divide-y divide-gray-100">
        {(data.opportunities || []).map((o) => (
          <div key={o.recommendation_id} className="p-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">{o.action}</span>
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                {money(o.estimated_savings)}/yr
              </span>
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                {o.overlap_percentage}% overlap
              </span>
              <span className="ml-auto text-xs text-gray-500">ROI {o.roi_months}mo</span>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-600">
              <span className="rounded bg-red-50 px-2 py-1 font-medium text-red-700">
                {o.redundant.name}
              </span>
              <ArrowRight size={12} className="text-gray-400" />
              <span className="rounded bg-indigo-50 px-2 py-1 font-medium text-indigo-700">
                {o.primary.name}
              </span>
              <span className="ml-1">
                {num(o.redundant.quantity_in_use)} of {num(o.redundant.quantity_purchased)} seats active
              </span>
            </div>

            <p className="mt-2 text-xs leading-relaxed text-gray-600">{o.rationale}</p>

            <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-gray-500">
              <span>Technical risk: <strong className="text-gray-700">{o.technical_risk}</strong></span>
              <span>Business risk: <strong className="text-gray-700">{o.business_risk}</strong></span>
              <span>Effort: <strong className="text-gray-700">{o.migration_effort}</strong></span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ShelfwareCard({ data }) {
  return (
    <div className={CARD}>
      <Head icon={PackageX} title={`Shelfware — under ${data.thresholdPct}% utilisation`}
            right={<span className="text-xs font-semibold text-red-600">
              {money(data.totalWaste)}/yr wasted
            </span>} />
      <div style={{ overflowX: "auto" }}>
        <table className="min-w-full text-sm" style={{ width: "100%" }}>
          <thead className="bg-white">
            <tr className="text-left text-xs text-gray-500">
              {["Product", "Purchased", "In use", "Unused", "Utilisation", "Wasted/yr"].map((h) => (
                <th key={h} className="px-4 py-2 font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.items || []).map((it) => (
              <tr key={it.product_id} className="border-t border-gray-100">
                <td className="px-4 py-2">
                  <div className="font-medium text-gray-900">{it.name}</div>
                  <div className="text-xs text-gray-500">
                    {it.manufacturer} · {it.category}
                    {it.status === "Divest" && (
                      <span className="ml-1 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">
                        DIVEST
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2 tabular-nums text-gray-700">{num(it.purchased)}</td>
                <td className="px-4 py-2 tabular-nums text-gray-700">{num(it.inUse)}</td>
                <td className="px-4 py-2 tabular-nums font-medium text-red-600">{num(it.unused)}</td>
                <td className="px-4 py-2"><UtilBar pct={it.utilizationPct} /></td>
                <td className="px-4 py-2 tabular-nums font-semibold text-red-600">
                  {money(it.wastedAnnual)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function LicenseCard({ data }) {
  return (
    <div className={CARD}>
      <Head icon={KeyRound} title="Licence position"
            right={<span className="text-xs text-gray-600">
              {num(data.totalUnused)} idle of {num(data.totalPurchased)}
            </span>} />
      <div style={{ overflowX: "auto" }}>
        <table className="min-w-full text-sm" style={{ width: "100%" }}>
          <thead className="bg-white">
            <tr className="text-left text-xs text-gray-500">
              {["Product", "Purchased", "Deployed", "In use", "Utilisation", "Annual"].map((h) => (
                <th key={h} className="px-4 py-2 font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.items || []).map((it) => (
              <tr key={it.product_id} className="border-t border-gray-100">
                <td className="px-4 py-2">
                  <div className="font-medium text-gray-900">{it.name}</div>
                  <div className="text-xs text-gray-500">{it.manufacturer} · {it.category}</div>
                </td>
                <td className="px-4 py-2 tabular-nums text-gray-700">{num(it.purchased)}</td>
                <td className="px-4 py-2 tabular-nums text-gray-700">{num(it.deployed)}</td>
                <td className="px-4 py-2 tabular-nums text-gray-700">{num(it.inUse)}</td>
                <td className="px-4 py-2"><UtilBar pct={it.utilizationPct} /></td>
                <td className="px-4 py-2 tabular-nums text-gray-700">{money(it.annualCost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function RenewalCard({ data }) {
  return (
    <div className={CARD}>
      <Head icon={CalendarClock} title={`Renewals in the next ${data.windowDays} days`}
            right={<span className="text-xs font-semibold text-gray-700">
              {money(data.totalValue)}/yr
            </span>} />
      <div className="divide-y divide-gray-100">
        {(data.items || []).map((it) => (
          <div key={it.product_id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
            <div className="w-14 shrink-0 text-center">
              <div className={`text-lg font-bold tabular-nums ${
                it.daysUntil <= 30 ? "text-red-600" : "text-gray-900"}`}>
                {it.daysUntil}
              </div>
              <div className="text-[10px] uppercase text-gray-500">days</div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-gray-900">{it.name}</div>
              <div className="text-xs text-gray-500">
                {it.renewalDate} · {money(it.annualCost)}/yr · {it.utilizationPct}% used
              </div>
            </div>
            {/* A renewal with money already going to waste is the actionable one. */}
            {it.wastedAnnual > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800">
                <TrendingDown size={12} /> {money(it.wastedAnnual)} wasted
              </span>
            )}
            {it.status === "Divest" && (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-1 text-xs font-semibold text-red-700">
                <AlertTriangle size={12} /> Divest
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function TrmResultCard({ kind, data }) {
  if (!data) return null;
  if (kind === "consolidation") return <ConsolidationCard data={data} />;
  if (kind === "shelfware") return <ShelfwareCard data={data} />;
  if (kind === "licenses") return <LicenseCard data={data} />;
  if (kind === "renewals") return <RenewalCard data={data} />;
  return <SummaryCard data={data} />;
}
